import { type ComponentType } from 'react'

import ArtworkImageDefinition from '../components/definitions/artwork-image-definition'
import ArtworkSizeDefinition from '../components/definitions/artwork-size-definition'
import ExtrasDefinition from '../components/definitions/extras-definition'
import FrameStyleDefinition from '../components/definitions/frame-style-definition'
import GlassDefinition from '../components/definitions/glass-definition'
import PassepartoutDefinition from '../components/definitions/passepartout-definition'
import ArtworkImageStepPreview from '../components/previews/artwork-image-step-preview'
import ArtworkSizeStepPreview from '../components/previews/artwork-size-step-preview'
import ExtrasStepPreview from '../components/previews/extras-step-preview'
import FrameStyleStepPreview from '../components/previews/frame-style-step-preview'
import GlassStepPreview from '../components/previews/glass-step-preview'
import PassepartoutStepPreview from '../components/previews/passepartout-step-preview'
import { type StepPreviewProps } from '../components/previews/configuration-step-preview'

export type Step =
  | 'artworkSize'
  | 'frameStyle'
  | 'artworkImage'
  | 'passepartout'
  | 'glass'
  | 'extras'

export type StepState = 'opening' | 'open' | 'closing' | 'closed'

export const configurationStepTitles: Record<Step, string> = {
  artworkSize: 'Artwork Size',
  artworkImage: 'Artwork Preview',
  frameStyle: 'Frame Style',
  passepartout: 'Passepartout',
  glass: 'Glass',
  extras: 'Extras',
}

export const defaultStepStates: Record<Step, StepState> = {
  artworkSize: 'closed',
  artworkImage: 'closed',
  frameStyle: 'closed',
  passepartout: 'closed',
  glass: 'closed',
  extras: 'closed',
}

export interface StepDefinitionProps {
  number: number
  onClose: () => void
  className?: string
}

export interface StepDefinition {
  number: number
  step: Step
  previewComponent: ComponentType<StepPreviewProps>
  definitionComponent: ComponentType<StepDefinitionProps>
}

export const getStepDefinitions = (
  showExtrasStep: boolean,
): StepDefinition[] => {
  const stepDefinitions: StepDefinition[] = [
    {
      number: 1,
      step: 'artworkSize',
      previewComponent: ArtworkSizeStepPreview,
      definitionComponent: ArtworkSizeDefinition,
    },
    {
      number: 2,
      step: 'artworkImage',
      previewComponent: ArtworkImageStepPreview,
      definitionComponent: ArtworkImageDefinition,
    },
    {
      number: 3,
      step: 'frameStyle',
      previewComponent: FrameStyleStepPreview,
      definitionComponent: FrameStyleDefinition,
    },
    {
      number: 4,
      step: 'passepartout',
      previewComponent: PassepartoutStepPreview,
      definitionComponent: PassepartoutDefinition,
    },
    {
      number: 5,
      step: 'glass',
      previewComponent: GlassStepPreview,
      definitionComponent: GlassDefinition,
    },
  ]

  if (showExtrasStep) {
    stepDefinitions.push({
      number: 6,
      step: 'extras',
      previewComponent: ExtrasStepPreview,
      definitionComponent: ExtrasDefinition,
    })
  }

  return stepDefinitions
}

export const getAnimationFinishedStepStates = (
  configurationStep: Step | null,
): Record<Step, StepState> => ({
  artworkSize: configurationStep === 'artworkSize' ? 'open' : 'closed',
  artworkImage: configurationStep === 'artworkImage' ? 'open' : 'closed',
  frameStyle: configurationStep === 'frameStyle' ? 'open' : 'closed',
  passepartout: configurationStep === 'passepartout' ? 'open' : 'closed',
  glass: configurationStep === 'glass' ? 'open' : 'closed',
  extras: configurationStep === 'extras' ? 'open' : 'closed',
})
